import { useState, useEffect } from "react";
import axios from "axios";
import { X, Check, ArrowUpRight, ArrowLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PhoneField from "@/components/PhoneField";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const T = {
  camisetaPartido: ["2", "4", "6", "8", "10", "12", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"],
  short: ["5XS", "4XS", "3XS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "3XL"],
  camisetaEntreno: ["5XS", "4XS", "3XS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "3XL"],
  medias: ["XXS (30-34)", "S (34-38)", "M (38-42)", "L (42-46)", "XL (46-50)"],
  chandal: ["5XS", "4XS", "3XS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"],
};

const EMPTY = {
  nombre: "", apellido: "", email: "",
  phone_code: "+34", phone_number: "",
  talla_camiseta_partido: "",
  talla_short: "", talla_camiseta_entreno: "", talla_medias: "",
  talla_chaqueta_chandal: "", talla_pantalon_chandal: "",
  notas: "",
};

const STEPS = [
  { id: 1, label: "Tus datos" },
  { id: 2, label: "Camiseta partido" },
  { id: 3, label: "Ropa base" },
  { id: 4, label: "Complementos" },
];

export default function EquipacionModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) { setStep(1); setForm(EMPTY); setSuccess(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.nombre.trim()) return "Ingresá tu nombre.";
      if (!form.apellido.trim()) return "Ingresá tu apellido.";
      if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
        return "Ingresá un email válido.";
      if (!form.phone_number.trim()) return "Ingresá tu teléfono.";
    }
    if (step === 2) {
      if (!form.talla_camiseta_partido) return "Elegí la talla de la camiseta de partido.";
    }
    if (step === 3) {
      if (!form.talla_short) return "Elegí la talla del short.";
      if (!form.talla_camiseta_entreno) return "Elegí la talla de la camiseta de entrenamiento.";
      if (!form.talla_medias) return "Elegí la talla de las medias.";
    }
    if (step === 4) {
      if (!form.talla_chaqueta_chandal) return "Elegí la talla de la chaqueta de chándal.";
      if (!form.talla_pantalon_chandal) return "Elegí la talla del pantalón de chándal.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => s - 1);

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/equipacion`, {
        ...form,
        telefono: `${form.phone_code} ${form.phone_number}`,
      });
      setSuccess(true);
    } catch {
      toast.error("No se pudo registrar la solicitud. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 bg-[#030712]/85 backdrop-blur-md"
      />

      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-[2rem] bg-[#0A1121] border border-white/10 shadow-2xl">
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-5 right-5 z-10 h-10 w-10 rounded-full grid place-items-center bg-[#030712]/60 backdrop-blur border border-white/10 text-white/70 hover:text-[#FFB800] hover:border-[#FFB800] transition-colors"
        >
          <X size={16} />
        </button>

        {success ? (
          <SuccessScreen onClose={onClose} nombre={form.nombre} />
        ) : (
          <>
            {/* Header */}
            <div className="p-6 pb-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-1">
                Pack Jugador · 160€
              </p>
              <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                Solicitud de equipación
              </h2>

              {/* Progress */}
              <div className="mt-5 flex items-center gap-2">
                {STEPS.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-2 flex-1">
                    <div className={`flex items-center gap-2 ${step === s.id ? "text-[#FFB800]" : step > s.id ? "text-white/50" : "text-white/20"}`}>
                      <span className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold border flex-shrink-0 ${
                        step > s.id
                          ? "bg-[#FFB800]/20 border-[#FFB800]/40 text-[#FFB800]"
                          : step === s.id
                          ? "bg-[#FFB800] border-[#FFB800] text-[#030712]"
                          : "bg-transparent border-white/15 text-white/25"
                      }`}>
                        {step > s.id ? <Check size={10} strokeWidth={3} /> : s.id}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] hidden sm:block">
                        {s.label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`flex-1 h-px ${step > s.id ? "bg-[#FFB800]/30" : "bg-white/10"}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step content */}
            <div className="p-6">
              {step === 1 && <Step1 form={form} set={set} setForm={setForm} />}
              {step === 2 && <Step2 form={form} set={set} />}
              {step === 3 && <Step3 form={form} set={set} />}
              {step === 4 && <Step4 form={form} set={set} />}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex items-center justify-between gap-4">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={back}
                  className="flex items-center gap-2 text-white/50 hover:text-white text-[11px] uppercase tracking-[0.2em] transition-colors"
                >
                  <ArrowLeft size={14} />
                  Atrás
                </button>
              ) : <div />}

              {step < 4 ? (
                <button
                  type="button"
                  onClick={next}
                  className="btn-primary"
                >
                  Siguiente
                  <ChevronRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={loading}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Enviando..." : "Confirmar solicitud"}
                  <ArrowUpRight size={14} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SuccessScreen({ onClose, nombre }) {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-[#FFB800] grid place-items-center text-[#030712] mb-6">
        <Check size={28} strokeWidth={3} />
      </div>
      <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight mb-3">
        ¡Solicitud enviada, {nombre}!
      </h3>
      <p className="text-white/65 max-w-sm mx-auto leading-relaxed text-[15px]">
        Recibimos tu solicitud de equipación. El club se pondrá en contacto
        contigo para confirmar el pedido y coordinar el pago.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="btn-primary mt-8 inline-flex"
      >
        Volver a la tienda
        <ArrowUpRight size={14} />
      </button>
    </div>
  );
}

/* ── Steps ── */

function Step1({ form, set, setForm }) {
  return (
    <div className="space-y-1">
      <p className="text-white/45 text-[13px] mb-4">Primero, tus datos de contacto.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <Field label="Nombre" n="01" value={form.nombre} onChange={set("nombre")} placeholder="Juan" required />
        <Field label="Apellido" n="02" value={form.apellido} onChange={set("apellido")} placeholder="García" required />
        <Field label="Email" n="03" type="email" value={form.email} onChange={set("email")} placeholder="tu@email.com" required />
      </div>
      <PhoneField n="04"
        countryCode={form.phone_code} onCountryCode={(v) => setForm((p) => ({ ...p, phone_code: v }))}
        number={form.phone_number}    onNumber={(v) => setForm((p) => ({ ...p, phone_number: v }))}
      />
    </div>
  );
}

function Step2({ form, set }) {
  return (
    <div className="space-y-1">
      <p className="text-white/45 text-[13px] mb-4">Talla de tu camiseta de partido.</p>
      <div className="grid grid-cols-1 gap-x-6">
        <Select label="Talla camiseta partido" n="01" value={form.talla_camiseta_partido} onChange={set("talla_camiseta_partido")} options={T.camisetaPartido} placeholder="Elegir talla" required />
      </div>
    </div>
  );
}

function Step3({ form, set }) {
  return (
    <div className="space-y-1">
      <p className="text-white/45 text-[13px] mb-4">Tallas de ropa base.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <Select label="Short azul/negro" n="01" value={form.talla_short} onChange={set("talla_short")} options={T.short} placeholder="Elegir talla" required />
        <Select label="Camiseta de entrenamiento" n="02" value={form.talla_camiseta_entreno} onChange={set("talla_camiseta_entreno")} options={T.camisetaEntreno} placeholder="Elegir talla" required />
        <div className="sm:col-span-2">
          <Select label="Medias" n="03" value={form.talla_medias} onChange={set("talla_medias")} options={T.medias} placeholder="Elegir talla" required />
        </div>
      </div>
    </div>
  );
}

function Step4({ form, set }) {
  return (
    <div className="space-y-1">
      <p className="text-white/45 text-[13px] mb-4">Chándal y complementos.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <Select label="Chaqueta chándal" n="01" value={form.talla_chaqueta_chandal} onChange={set("talla_chaqueta_chandal")} options={T.chandal} placeholder="Elegir talla" required />
        <Select label="Pantalón chándal" n="02" value={form.talla_pantalon_chandal} onChange={set("talla_pantalon_chandal")} options={T.chandal} placeholder="Elegir talla" required />
      </div>
      <div className="pt-2">
        <label className="flex justify-between items-baseline mb-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Notas adicionales</span>
          <span className="font-mono text-[10px] text-white/30">03</span>
        </label>
        <textarea
          rows={3}
          value={form.notas}
          onChange={set("notas")}
          placeholder="Cualquier comentario adicional"
          className="field-input resize-none"
        />
      </div>
    </div>
  );
}

/* ── Primitives ── */

function Field({ label, n, value, onChange, placeholder, type = "text", required }) {
  return (
    <div className="pt-4">
      <label className="flex justify-between items-baseline mb-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
          {label}{required && <span className="text-[#FFB800]"> *</span>}
        </span>
        <span className="font-mono text-[10px] text-white/30">{n}</span>
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="field-input"
      />
    </div>
  );
}

function Select({ label, n, value, onChange, options, placeholder, required }) {
  return (
    <div className="pt-4">
      <label className="flex justify-between items-baseline mb-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
          {label}{required && <span className="text-[#FFB800]"> *</span>}
        </span>
        <span className="font-mono text-[10px] text-white/30">{n}</span>
      </label>
      <select value={value} onChange={onChange} className="field-input bg-[#030712]">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
